import { FinvuService } from './operations/backend';
import { MessageBuilder, FinvuConstants, } from './messages';
import { filter, firstValueFrom, map, tap } from 'rxjs';
import moment from 'moment';
import { v4 as uuidv4 } from 'uuid';
import { getStorageMechanism } from '@finarkein/aasdk-core';
import { ResponseMapperFinvu } from './ResponseMapperFinvu';
export class FinvuWebSdk {
    constructor() {
        this.availableStorage = getStorageMechanism();
        this.sendRawResponse = false;
        // TODO: configure the identifier from external configuration
        this.identifier = () => 'finvu';
        this.checkReadiness = (resolve) => {
            var _a;
            if ((_a = this.backend) === null || _a === void 0 ? void 0 : _a.isReady) {
                resolve();
            }
            else {
                setTimeout(() => this.checkReadiness(resolve), 500);
            }
        };
    }
    cleanUp() {
        this.availableStorage.removeAllValues();
    }
    async setup(initOptions) {
        this.backend = new FinvuService(initOptions);
        if (initOptions.rawResponse) {
            this.sendRawResponse = true;
        }
        await this.backend.connect(); // Trigger websocket connection
        return new Promise((resolve) => this.checkReadiness(resolve));
    }
    authInit(input) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        let msg;
        let init;
        if (input.encryptedRequest && input.encryptedFiuId && input.requestDate) {
            msg = MessageBuilder.build('urn:finvu:in:app:req.webViewloginOtp.01', input, this.availableStorage);
            init = this.backend.on('urn:finvu:in:app:res.webViewloginOtp.01');
        }
        else {
            msg = MessageBuilder.build('urn:finvu:in:app:req.loginOtp.01', input, this.availableStorage);
            init = this.backend.on('urn:finvu:in:app:res.loginOtp.01');
        }
        if (init) {
            this.backend.send(msg);
            const res$ = init.pipe(filter((r) => r.header.mid === msg.header.mid), map((res) => res.payload));
            // Deliver the complete payload back
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('VALIDATE_USER');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to send mobile OTP');
    }
    authVerify(input) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.loginOtpVerify.01', input, this.availableStorage);
        const verify = this.backend.on('urn:finvu:in:app:res.loginOtpVerify.01');
        if (verify) {
            this.backend.send(msg);
            const res$ = verify.pipe(tap((res) => {
                const msg = res;
                const sid = msg.header.sid;
                this.availableStorage.setValue(FinvuConstants.SID, sid);
                // If status is success, store the User ID.
                const status = msg.payload.status;
                if (status === 'SUCCESS') {
                    this.availableStorage.setValue(FinvuConstants.UID, msg.payload.userId);
                }
                // TODO: Do something with other status codes
            }), filter((r) => r.header.mid === msg.header.mid), map((res) => res.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('AA_LOGIN');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to verify OTP');
    }
    mobileAuthInit(input) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.mobileVerification.01', input, this.availableStorage);
        const init = this.backend.on('urn:finvu:in:app:res.mobileVerification.01');
        if (init) {
            this.backend.send(msg);
            const res$ = init.pipe(filter((r) => r.header.mid === msg.header.mid), map((res) => res.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('ALT_MOBILE');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to init OTP for additional mobile number');
    }
    mobileAuthVerify(input) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.mobileVerificationVerify.01', input, this.availableStorage);
        const verify = this.backend.on('urn:finvu:in:app:res.mobileVerificationVerify.01');
        if (verify) {
            this.backend.send(msg);
            const res$ = verify.pipe(filter((r) => r.header.mid === msg.header.mid), map((res) => res.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('CONFIRM_ALT_MOBILE');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to verify OTP for additional mobile number');
    }
    getLinkedAccounts() {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        // auto-read the user identifier from stored session
        const input = { userId: this.availableStorage.getValue(FinvuConstants.UID) };
        const msg = MessageBuilder.build('urn:finvu:in:app:req.userLinkedAccount.01', input, this.availableStorage);
        const resHandle = this.backend.on('urn:finvu:in:app:res.userLinkedAccount.01');
        if (resHandle) {
            this.backend.send(msg);
            const res$ = resHandle.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('LINKED_ACCOUNTS');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to obtain linked accounts');
    }
    discoverAccounts(input) {
        input.ver = FinvuConstants.VER;
        input.txnid = uuidv4();
        input.timestamp = moment().format();
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const knownId = this.availableStorage.getValue(FinvuConstants.UID);
        if (knownId && input.Customer.id !== knownId) {
            console.warn(`[${Date.now()}] ${this.identifier}:discover - fixing 'customer.id' from store`);
            input.Customer.id = knownId;
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.discover.01', input, this.availableStorage);
        const resHandle = this.backend.on('urn:finvu:in:app:res.discover.01');
        if (resHandle) {
            this.backend.send(msg);
            const res$ = resHandle.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('ACCOUNT_DISCOVERY');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to discover accounts');
    }
    linkAccounts(input) {
        input.ver = FinvuConstants.VER;
        input.txnid = uuidv4();
        input.timestamp = moment().format();
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const knownId = this.availableStorage.getValue(FinvuConstants.UID);
        if (knownId && input.Customer.id !== knownId) {
            console.warn(`[${Date.now()}] ${this.identifier}:linking - fixing 'customer.id' from store`);
            input.Customer.id = knownId;
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.linking.01', input, this.availableStorage);
        const resHandle = this.backend.on('urn:finvu:in:app:res.linking.01');
        if (resHandle) {
            this.backend.send(msg);
            const res$ = resHandle.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('ACCOUNT_LINKING');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to discover accounts');
    }
    confirmLinking(input) {
        input.ver = FinvuConstants.VER;
        input.txnid = uuidv4();
        input.timestamp = moment().format();
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.confirm-token.01', input, this.availableStorage);
        const resHandle = this.backend.on('urn:finvu:in:app:res.confirm-token.01');
        if (resHandle) {
            this.backend.send(msg);
            const res$ = resHandle.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('CONFIRM_ACCOUNT_LINKING');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to confirm account linking');
    }
    consentRequestDetails(consentHandleId) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const input = {
            consentHandleId,
            userId: this.availableStorage.getValue(FinvuConstants.UID),
        };
        const msg = MessageBuilder.build('urn:finvu:in:app:req.consentRequestDetails.01', input, this.availableStorage);
        const resHandler = this.backend.on('urn:finvu:in:app:res.consentRequestDetailsResponse.01');
        if (resHandler) {
            this.backend.send(msg);
            const res$ = resHandler.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('CONSENT_DETAILS');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to confirm account linking');
    }
    consent(input) {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const msg = MessageBuilder.build('urn:finvu:in:app:req.accountConsentRequest.01', input, this.availableStorage);
        const resHandler = this.backend.on('urn:finvu:in:app:res.accountConsentRequest.01');
        if (resHandler) {
            this.backend.send(msg);
            const res$ = resHandler.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('CONSENT_ACTION');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        //return Promise.reject('Failed to confirm consent action');
    }
    logout() {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        const input = {
            userId: this.availableStorage.getValue(FinvuConstants.UID),
        };
        const msg = MessageBuilder.build('urn:finvu:in:app:req.logout.01', input, this.availableStorage);
        const resHandler = this.backend.on('urn:finvu:in:app:res.logout.01');
        if (resHandler) {
            this.backend.send(msg);
            const res$ = resHandler.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('LOGOUT');
        }
        return Promise.reject('Failed to confirm consent action');
    }
    getUserInfo() {
        if (!this.backend) {
            return Promise.reject('Finvu SDK not initialized, please call #setup first.');
        }
        // auto-read the user identifier from stored session
        const input = { userId: this.availableStorage.getValue(FinvuConstants.UID) };
        const msg = MessageBuilder.build('urn:finvu:in:app:req.userInfo.01', input, this.availableStorage);
        const resHandle = this.backend.on('urn:finvu:in:app:res.userInfo.01');
        if (resHandle) {
            this.backend.send(msg);
            const res$ = resHandle.pipe(filter((r) => r.header.mid === msg.header.mid), map((r) => r.payload));
            if (this.sendRawResponse) {
                return firstValueFrom(res$);
            }
            return new ResponseMapperFinvu(firstValueFrom(res$)).getResponse('USER_INFO');
        }
        else {
            return Promise.reject('Session Error Occured!! Please restart the journey');
        }
        return Promise.reject('Failed to obtain linked accounts');
    }
}
//# sourceMappingURL=aasdk-finvu.js.map